import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { computed, EventEmitter, inject, Service, OnDestroy, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { KDateAdd, KDateNow } from '@kiwid-app/kiwid-date-typescript';
import { IEmployee } from 'app/core/models/employee/employee';
import { IError } from 'app/core/models/error/error';
import { IClinic, IUser } from 'app/core/models/user/user';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { KiwidPermissionsService } from 'app/core/permissions/service/kiwid-permissions.service';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, Observable, Subscription, throwError } from 'rxjs';
import { catchError, last, map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { EKiwidPlanStatus } from '../../enums/location.enum';
import {
    EAuthenticationStorage,
    IAuthentication,
    IForgotPassword,
    ILogin,
    IRefreshToken,
    IResetPassword,
    IVerifyEmail,
} from '../../models/authentication/authentication';
import { ILaboratory } from '../../models/laboratory/laboratory';
import { ILocation } from '../../models/location/location';
import { EAccessType } from '../../permissions/enum/access.enum';
import { IAccess } from '../../permissions/model/access';
import { UserDeviceService } from '../user/user-device.service';
import { UserService } from '../user/user.service';

export const user = () => inject(AuthenticationService).user();

const urlVerifyEmail = () => ['auth', 'verify-email'];

@Service()
export class AuthenticationService implements OnDestroy {
    // inject
    private readonly navigationService = inject(NavigationService);
    private readonly router = inject(Router);
    private readonly httpClient = inject(HttpClient);
    private readonly cookieService = inject(CookieService);
    private readonly userDeviceService = inject(UserDeviceService);
    private readonly userService = inject(UserService);
    private readonly kiwidPermissionsService = inject(KiwidPermissionsService);
    private readonly matDialog = inject(MatDialog);

    private isRedirectLogout = false;
    public readonly user = signal<IUser | null>(null);
    public readonly lang = signal<string>('pt-BR');
    public readonly clinicList = signal<IClinic[]>([]);
    public readonly clinicSelected = signal<IClinic | null>(null);
    public readonly clinicId = signal<string | null>(this.cookieService.get(EAuthenticationStorage.CLINIC_ID) ?? null);
    public readonly permissions = computed<IAccess[]>(() => this.kiwidPermissionsService.permissions());

    // LAB
    public readonly laboratorySelected = signal<ILaboratory | null>(null);
    public readonly employee = signal<Pick<IEmployee, 'id' | 'isCommissioned'> | null>(null);
    public readonly laboratoryId = signal<string | null>(this.cookieService.get(EAuthenticationStorage.LABORATORY_ID) ?? null);
    public readonly locationIds = signal<string[]>([]);
    public readonly locations = signal<ILocation[]>([]);

    public readonly locationsActive = computed(() => {
        return this.locations()
            .filter((location) => {
                const kiwidPlan = location.kiwidPlans.find((kiwidPlanFind) => kiwidPlanFind.isCurrent === true);
                return [EKiwidPlanStatus.CREATED, EKiwidPlanStatus.ACTIVE, EKiwidPlanStatus.BLOCKED].includes(kiwidPlan?.status);
            })
            .sort((location) => (location.isDefault ? 1 : 0));
    });
    public readonly locationDefault = computed<ILocation | null>(() => {
        return (this.locationsActive().find((location) => location.isDefault) ?? this.locationsActive().length > 0)
            ? this.locationsActive()[0]
            : null;
    });

    public readonly clinicSelectedEmit = new BehaviorSubject<IClinic | null>(this.clinicSelected());
    private readonly subscriptions = new Subscription();
    public readonly eventRefreshToken = new EventEmitter<IAuthentication>();

    constructor() {
        const user = localStorage.getItem(EAuthenticationStorage.USER);
        this.user.set(!user ? null : JSON.parse(user));

        const lang = localStorage.getItem(EAuthenticationStorage.LANG);
        this.lang.set(lang ? (lang === 'pt' ? 'pt-BR' : lang) : 'pt-BR');

        const locationIds = localStorage.getItem(EAuthenticationStorage.LOCATION_IDS);
        this.locationIds.set(locationIds ? JSON.parse(locationIds) : []);

        const locations = localStorage.getItem(EAuthenticationStorage.LOCATIONS);
        this.locations.set(!locations ? [] : JSON.parse(locations));
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    public get userIsKiwid(): boolean {
        return false; // !!this.token ? JwtHelper.getJwtPayload(this.token)?.kiwid ?? false : false
    }

    public get token(): string | null {
        return this.cookieService.get(EAuthenticationStorage.TOKEN);
    }

    public get refreshToken(): string | null {
        return this.cookieService.get(EAuthenticationStorage.REFRESH_TOKEN);
    }

    public setClinicList(clinicList: IClinic[]): void {
        this.clinicList.set(clinicList);
    }

    public setClinicSelected(clinic: IClinic | null) {
        this.clinicSelected.set(clinic);

        localStorage.removeItem(EAuthenticationStorage.CLINIC);
        localStorage.removeItem(EAuthenticationStorage.CLINIC_ID);

        if (!!clinic) {
            localStorage.setItem(EAuthenticationStorage.CLINIC, JSON.stringify(clinic));
            localStorage.setItem(EAuthenticationStorage.CLINIC_ID, clinic.id);
            this.setUserOwner(clinic.ownerId === this.user()?.id);
        }

        this.clinicSelectedEmit.next(clinic);
    }

    public setPermissions(permissions: IAccess[]) {
        this.kiwidPermissionsService.setPermissions(permissions);
    }

    public redirectUser(toRedirectUser: boolean = false): void {
        if (this.token) {
            if (!this.clinicId() || toRedirectUser) {
                this.router.navigate(['/clinic/user-access']).then();
                return;
            }
        }
    }

    public checkRouteToRedirectUser(): void {
        const subscription = new Subscription();
        subscription.add(
            this.kiwidPermissionsService.initialize.subscribe((initialize) => {
                if (initialize) {
                    this.subscriptions.add(
                        this.navigationService
                            .get()
                            .pipe(last())
                            .subscribe({
                                next: (nav) => {
                                    const navigation = [...nav.default];
                                    const navigate = (item: FuseNavigationItem): boolean => {
                                        if (!item.children || item.children?.length === 0) {
                                            this.router.navigate([`/${item.link}`]).then();
                                            return false;
                                        }

                                        const children = item.children.find(
                                            (childrenFind) =>
                                                !childrenFind.permissions || this.kiwidPermissionsService.hasPermission(childrenFind.permissions),
                                        );
                                        if (children) {
                                            this.router.navigate([`/${children.link}`]).then();
                                            return false;
                                        }

                                        return true;
                                    };

                                    const notNavigate = navigation.every((nav) => {
                                        if (!nav.permissions) {
                                            return navigate(nav);
                                        }

                                        if (this.kiwidPermissionsService.hasPermission(nav.permissions)) {
                                            return navigate(nav);
                                        }

                                        return true;
                                    });

                                    if (notNavigate) {
                                        this.router.navigate(['/report']).then();
                                    }
                                },
                                error: (error) => {
                                    console.log(`Error in redirect page ${error}`);
                                },
                            }),
                    );
                    subscription.unsubscribe();
                }
            }),
        );
    }

    public setClinicId(clinicId: string): void {
        this.clinicId.set(clinicId);
        this.cookieService.delete(EAuthenticationStorage.CLINIC_ID);
        this.cookieService.set(EAuthenticationStorage.CLINIC_ID, clinicId, KDateAdd(KDateNow(), 'years', 2), '/', `.${environment.appDomain}`);
    }

    public getStoredClinicId(): string | null {
        return this.cookieService.get(EAuthenticationStorage.CLINIC_ID) || null;
    }

    public setUserOwner(isOwner: boolean): void {
        localStorage.setItem(EAuthenticationStorage.USER, JSON.stringify({ ...this.user(), isOwner }));
        this.user.update((data) => ({ ...data, isOwner }));
    }

    public setClinic(clinic: IClinic): void {
        this.clinicSelected.set(clinic);
        this.clinicId.set(clinic.id);

        localStorage.removeItem(EAuthenticationStorage.CLINIC_ID);
        localStorage.setItem(EAuthenticationStorage.CLINIC_ID, clinic.id);

        localStorage.removeItem(EAuthenticationStorage.CLINIC);
        localStorage.setItem(EAuthenticationStorage.CLINIC, JSON.stringify(clinic));
    }

    public getStoredUser(): IUser | null {
        const user = localStorage.getItem(EAuthenticationStorage.USER);
        return !!user ? JSON.parse(user) : null;
    }

    public setUser(user: IUser, isRedirect = true): void {
        localStorage.removeItem(EAuthenticationStorage.USER);
        localStorage.setItem(EAuthenticationStorage.USER, JSON.stringify(user));

        if (user?.clinicsCount <= 0 && isRedirect) {
            this.router.navigate(['/clinic/user-access']).then();
        } else {
            if (isRedirect) {
                if (!!user.clinic && !this.clinicId()) {
                    if (user.clinicsCount > 1) {
                        this.router.navigate(['/clinic/user-access']).then();
                    } else {
                        this.setClinic(user.clinic);
                        this.router.navigate(['/production/order']).then();
                    }
                }
            }
        }

        this.user.set(user);
    }

    public userCookie(data: IAuthentication): void {
        // remove old cookies
        this.cookieService.delete(EAuthenticationStorage.TOKEN, '/', `.${environment.appDomain}`);
        this.cookieService.delete(EAuthenticationStorage.TIME_TOKEN, '/', `.${environment.appDomain}`);
        this.cookieService.delete(EAuthenticationStorage.REFRESH_TOKEN, '/', `.${environment.appDomain}`);
        // insert new cookies
        this.cookieService.set(EAuthenticationStorage.TOKEN, data.accessToken, KDateAdd(KDateNow(), 'years', 2), '/', `.${environment.appDomain}`);
        this.cookieService.set(
            EAuthenticationStorage.REFRESH_TOKEN,
            data.refreshToken,
            KDateAdd(KDateNow(), 'years', 2),
            '/',
            `.${environment.appDomain}`,
        );
        this.cookieService.set(
            EAuthenticationStorage.TIME_TOKEN,
            KDateAdd(KDateNow(), 'seconds', data.expiresIn).toISOString(),
            KDateAdd(KDateNow(), 'years', 2),
            '/',
            `.${environment.appDomain}`,
        );
    }

    public async logout(isRedirect = true): Promise<void> {
        if (this.isRedirectLogout) {
            return;
        }

        try {
            await this.userDeviceService.remove();
        } catch (error) {
            console.log(error);
        }

        this.cookieService.delete(EAuthenticationStorage.TOKEN, '/', `.${environment.appDomain}`);
        this.cookieService.delete(EAuthenticationStorage.REFRESH_TOKEN, '/', `.${environment.appDomain}`);
        this.cookieService.delete(EAuthenticationStorage.TIME_TOKEN, '/', `.${environment.appDomain}`);
        this.cookieService.delete(EAuthenticationStorage.CLINIC_ID, '/', `.${environment.appDomain}`);
        this.cookieService.delete(EAuthenticationStorage.CLINICS, '/', `.${environment.appDomain}`);
        this.cookieService.delete(EAuthenticationStorage.USER, '/', `.${environment.appDomain}`);

        localStorage.removeItem(EAuthenticationStorage.TOKEN);
        localStorage.removeItem(EAuthenticationStorage.REFRESH_TOKEN);
        localStorage.removeItem(EAuthenticationStorage.CLINIC_ID);
        localStorage.removeItem(EAuthenticationStorage.CLINICS);
        localStorage.removeItem(EAuthenticationStorage.USER);

        this.setClinicSelected(null);
        this.clinicList.set([]);
        this.setPermissions([]);
        this.user.set(null);
        this.clinicId.set(null);
        this.kiwidPermissionsService.initialize.next(false);

        if (isRedirect) {
            // window.location.href = `//${window.location.host}/auth/login`;
            window.location.href = `/auth/login`;
            // this.isRedirectLogout = true;
            return;
        }

        this.matDialog?.closeAll();
    }

    public loginUser(data: IAuthentication): Observable<IUser> {
        this.userCookie(data);
        // get user
        return this.userService.me(true) as Observable<IUser>;
    }

    public login(parameters: ILogin): Observable<IAuthentication> {
        const baseUrl = ['auth', 'sign-in'];

        const url = this.router.createUrlTree(baseUrl);

        const parsedUrl = `${environment.apiUrl}/${environment.apiVersion}${url}`;

        return this.httpClient
            .post(
                parsedUrl,
                {
                    ...parameters,
                    type: EAccessType.ADMIN_CLINIC,
                },
                { responseType: 'json' },
            )
            .pipe(
                map((res: IAuthentication) => {
                    return res;
                }),
                catchError((error: HttpErrorResponse) => {
                    if (error.error instanceof ErrorEvent) {
                        return throwError(() => `Error: ${error.error.message}`);
                    }
                    const errorApi: IError = error.error.error;
                    return throwError(() => errorApi);
                }),
            );
    }

    public getRefreshToken(parameters: IRefreshToken): Observable<IAuthentication> {
        const baseUrl = ['auth', 'refresh-token'];

        const url = this.router.createUrlTree(baseUrl);

        const parsedUrl = `${environment.apiUrl}/${environment.apiVersion}${url}`;

        return this.httpClient
            .post(
                parsedUrl,
                {
                    ...parameters,
                    type: EAccessType.ADMIN_CLINIC,
                },
                { responseType: 'json' },
            )
            .pipe(
                map((res: IAuthentication) => {
                    this.eventRefreshToken.emit(res);
                    return res;
                }),
                catchError((error: HttpErrorResponse) => {
                    if (error.error instanceof ErrorEvent) {
                        return throwError(() => `Error: ${error.error.message}`);
                    }
                    const errorApi: IError = error.error.error;
                    return throwError(() => errorApi);
                }),
            );
    }

    public forgotPassword(parameters: IForgotPassword): Observable<null> {
        const baseUrl = ['auth', 'forgot'];

        const url = this.router.createUrlTree(baseUrl);

        const parsedUrl = `${environment.apiUrl}/${environment.apiVersion}${url}`;

        return this.httpClient.post(parsedUrl, parameters, { responseType: 'json' }).pipe(
            map((res: null) => {
                return res;
            }),
            catchError((error: HttpErrorResponse) => {
                if (error.error instanceof ErrorEvent) {
                    return throwError(() => `Error: ${error.error.message}`);
                }
                const errorApi: IError = error.error.error;
                return throwError(() => errorApi);
            }),
        );
    }

    public resetPassword({ token, ...rest }: IResetPassword): Observable<null> {
        const baseUrl = ['auth', 'reset', token];

        const url = this.router.createUrlTree(baseUrl);

        const parsedUrl = `${environment.apiUrl}/${environment.apiVersion}${url}`;

        return this.httpClient.put(parsedUrl, rest, { responseType: 'json' }).pipe(
            map((res: null) => {
                return res;
            }),
            catchError((error: HttpErrorResponse) => {
                if (error.error instanceof ErrorEvent) {
                    return throwError(() => `Error: ${error.error.message}`);
                }
                const errorApi: IError = error.error.error;
                return throwError(() => errorApi);
            }),
        );
    }

    public verifyEmail(data: IVerifyEmail): Observable<null> {
        const url = this.router.createUrlTree(urlVerifyEmail());

        const parsedUrl = `${environment.apiUrl}/${environment.apiVersion}${url}`;

        return this.httpClient.post<null>(parsedUrl, data, { responseType: 'json' }).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.error instanceof ErrorEvent) {
                    return throwError(() => `Error: ${error.error.message}`);
                }
                const errorApi: IError = error.error.error;
                return throwError(() => errorApi);
            }),
        );
    }
}
